import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import exportingInit from 'highcharts/modules/exporting';
import exportDataInit from 'highcharts/modules/export-data';

// Initialize the exporting and export data modules
exportingInit(Highcharts);
exportDataInit(Highcharts);

const ForecastChart = ({ data, days = 1, type = 'consumption' }) => {
    if (!data || !data.length) {
        return null;
    }

    const isConsumption = type === 'consumption';

    // Transform data based on type
    const transformedBaseline = data.map(
        item => isConsumption ? item.baseline_consumption : item.baseline_production
    );
    const transformedFlexUp = data.map(
        item => isConsumption ? item.flexibility_up_consumption : item.flexibility_up_production
    );
    const transformedFlexDown = data.map(
        item => isConsumption ? item.flexibility_down_consumption : item.flexibility_down_production
    );

    // Use different colors for consumption vs production
    const chartColor = isConsumption
        ? Highcharts.getOptions().colors[0]
        : Highcharts.getOptions().colors[2];

    let xAxisCategories;
    let plotBands = [];
    let transformedFlexRange;

    if (days === 1) {
        // Single day logic
        xAxisCategories = data.map(item => `${item.hour}:00`);
        transformedFlexRange = data.map((item, index) => [
            item.hour,
            transformedFlexDown[index],
            transformedFlexUp[index]
        ]);
    } else {
        // Multiple days logic
        xAxisCategories = data.map(
            item => `${String(item.hour).padStart(2, '0')}:00`
        );

        // Identify the indices where each day starts
        let dayStartIndices = [];
        let previousDay = null;
        data.forEach((item, index) => {
            if (item.day !== previousDay) {
                previousDay = item.day;
                dayStartIndices.push({ day: item.day, index });
            }
        });

        // Create plot bands for each day
        plotBands = dayStartIndices.map((dayInfo, i) => {
            const from = dayInfo.index - 0.5;
            const to =
                i + 1 < dayStartIndices.length
                    ? dayStartIndices[i + 1].index - 0.5
                    : data.length - 0.5;

            return {
                color: i % 2 === 0 ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0)',
                from,
                to,
                label: {
                    text: `Day ${i + 1}`,
                    align: 'center',
                    verticalAlign: 'top',
                    y: 20,
                    style: {
                        color: 'gray',
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }
                }
            };
        });

        // Map arearange data for multiple days
        transformedFlexRange = data.map((item, index) => [
            index,
            transformedFlexDown[index],
            transformedFlexUp[index]
        ]);
    }

    // Dynamic labels based on type
    const yAxisLabel = isConsumption ? 'Consumption (kWh)' : 'Production (kWh)';
    const baselineName = isConsumption ? 'Baseline Consumption' : 'Baseline Production';
    const flexibilityName = isConsumption ? 'Flexibility Consumption Range' : 'Flexibility Production Range';

    const chartOptions = {
        chart: {
            type: 'line'
        },
        title: {
            text: null
        },
        xAxis: {
            categories: xAxisCategories,
            title: {
                text: 'Hour'
            },
            plotBands: plotBands
        },
        yAxis: {
            gridLineWidth: 0,
            title: {
                text: yAxisLabel
            }
        },
        tooltip: {
            crosshairs: true,
            shared: true,
            valueSuffix: ' kWh'
        },
        series: [
            {
                name: baselineName,
                type: 'line',
                dashStyle: 'Dash',
                data: transformedBaseline,
                color: chartColor,
                marker: {
                    enabled: false
                }
            },
            {
                name: flexibilityName,
                type: 'arearange',
                data: transformedFlexRange,
                color: chartColor,
                fillOpacity: 0.3,
                lineWidth: 0,
                marker: {
                    enabled: false
                }
            }
        ],
        exporting: {
            enabled: true
        },
        credits: {
            enabled: false
        },
        accessibility: {
            enabled: false
        }
    };

    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: '100%', width: '100%' } }}
        />
    );
};

export default ForecastChart; 